import React, { useState, useEffect, FormEvent } from 'react';
import {
  getTeamMembers,
  editTeamMember,
  getMemberInfo,
} from '@api/team-member';
import { ApiResponse, ApiResponseArray } from '@typings/ApiResponse';
import {
  CarehomeTeamMemberData,
  CarehomeStaff,
} from '@typings/TeamMemberTypes';
import useAuth from '@hooks/useAuth';
import { roles } from '@typings/ProfileTypes';
import { FormError, SubmitStatus, SubmitStatusEnum } from '@typings/common';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import ErrorSummary from '@components/ErrorSummary';
import { usePartner } from '@contexts/PartnerContext';
import getErrorMessage from '@utils/errorUtils';
import JobTitle from './JobTitle';
import Speciality from './Speciality';
import TeamName from './TeamName';
import { TeamMemberInfo } from './utils';

interface EditTeamMemberProps {
  partnerCarehomeId: string;
  isFormSubmitted: boolean;
  selectedTeamMemberId: string;
  setSelectedTeamMemberId: (id: string) => void;
  hasError: boolean;
}

interface TeamMemberOptions {
  value: string;
  label: string;
}

const EditTeamMember = ({
  partnerCarehomeId,
  isFormSubmitted,
  selectedTeamMemberId,
  setSelectedTeamMemberId,
  hasError,
}: EditTeamMemberProps) => {
  const [teamMemberList, setTeamMemberList] = useState<TeamMemberOptions[]>([]);

  const { profile } = useAuth();
  const [userId, setUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectMemberError, setSelectMemberError] = useState(false);
  const [errors, setErrors] = useState<FormError[]>([]);
  const [memberInfo, setMemberInfo] = useState<TeamMemberInfo>({
    jobTitle: '',
    speciality: '',
    teamName: '',
  });
  const [editTeamMemberStatus, setEditTeamMemberStatus] =
    useState<SubmitStatus>({
      loading: false,
      submitted: SubmitStatusEnum.IDLE,
      message: '',
    });

  const { selectedPartnerId } = usePartner();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Reset previous errors
    setSelectMemberError(false);

    let hasEditError = false;

    // Check for empty fields and validate email format
    if (!selectedTeamMemberId) {
      setSelectMemberError(true);
      hasEditError = true;
    }

    // If any validation errors exist, prevent form submission
    if (hasEditError) {
      return;
    }

    // If no errors, proceed with form submission
    setEditTeamMemberStatus(prev => ({ ...prev, loading: true }));
    try {
      const response: ApiResponseArray<CarehomeStaff | string> =
        await editTeamMember(
          selectedTeamMemberId,
          selectedRole,
          partnerCarehomeId,
          memberInfo
        );
      if (response) {
        if (response.status === 'success') {
          setEditTeamMemberStatus(prev => ({
            ...prev,
            submitted: SubmitStatusEnum.SUCCESS,
            message: 'These details have been saved',
          }));
          setTimeout(() => {
            setEditTeamMemberStatus(prev => ({
              ...prev,
              submitted: SubmitStatusEnum.IDLE,
              message: '',
            }));
          }, 3000);
        }
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);
      setEditTeamMemberStatus({
        loading: false,
        submitted: SubmitStatusEnum.FAILED,
        message: errorMessage,
      });
    } finally {
      setEditTeamMemberStatus(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    if (profile) {
      setUserId(profile.id);
    }
  }, [profile]);

  useEffect(() => {
    const getTeamMembersList = async () => {
      if (selectedPartnerId) {
        try {
          const response: ApiResponseArray<CarehomeTeamMemberData> =
            await getTeamMembers(selectedPartnerId);
          if (response && response.status === 'success') {
            const transformedData: TeamMemberOptions[] = response?.data?.map(
              (staff: any) => ({
                value: staff.carehome_staff.id,
                label: staff.carehome_staff.full_name,
              })
            );

            const filterdData = transformedData.filter(
              user => user.value !== userId
            );
            setTeamMemberList(filterdData);
          }
        } catch (error) {
          console.error(error);
        }
      }
    };

    getTeamMembersList();
  }, [selectedPartnerId, isFormSubmitted, userId]);

  useEffect(() => {
    const newErrors: FormError[] = [];
    if (selectMemberError || hasError) {
      newErrors.push({
        id: 'selectedRole',
        message: 'Please select a person you would like to Edit or Remove',
      });
    }

    setErrors(newErrors);
  }, [selectMemberError, hasError]);

  useEffect(() => {
    setSelectedTeamMemberId('');
  }, [teamMemberList, setSelectedTeamMemberId]);

  useEffect(() => {
    if (selectedTeamMemberId) {
      setSelectMemberError(false);
      const getTeamMemberInfo = async () => {
        if (selectedPartnerId) {
          try {
            const response: ApiResponse<CarehomeTeamMemberData> =
              await getMemberInfo(selectedTeamMemberId, selectedPartnerId);
            if (response && response.status === 'success') {
              const {
                speciality,
                team_name: teamName,
                job_title: jobTitle,
                carehome_staff: carehomeStaff,
              } = response.data;
              setMemberInfo({
                speciality: speciality ?? '',
                jobTitle: jobTitle ?? '',
                teamName: teamName ?? '',
              });
              setSelectedRole(carehomeStaff?.role as string);
            }
          } catch (error) {
            console.error(error);
          }
        }
      };

      getTeamMemberInfo();
    }
  }, [selectedTeamMemberId, selectedPartnerId]);

  return (
    <div className="edit-team-member">
      <h3 className="govuk-heading-m">Remove / Edit Team Members & Managers</h3>

      <form onSubmit={handleSubmit}>
        {errors.length > 0 && <ErrorSummary errors={errors} />}
        {editTeamMemberStatus.submitted === SubmitStatusEnum.SUCCESS && (
          <SuccessNotificationBanner
            content={editTeamMemberStatus.message}
            title=""
            duration={3000}
            noScrollToTop
          />
        )}
        {editTeamMemberStatus.submitted === SubmitStatusEnum.FAILED && (
          <ErrorSummary
            errors={[
              { id: 'editTeamMember', message: editTeamMemberStatus.message },
            ]}
          />
        )}
        <div
          className={`govuk-form-group ${selectMemberError || hasError ? 'govuk-form-group--error' : ''}`}
        >
          {selectMemberError ||
            (hasError && (
              <p id="role-error" className="govuk-error-message">
                <span className="govuk-visually-hidden">Error:</span> Please
                select Team Member or Manager you would like to Edit or Remove
              </p>
            ))}

          <select
            className={`govuk-select govuk-!-width-full ${selectMemberError || hasError ? 'govuk-select--error' : ''}`}
            id="selectedTeamMemberId"
            name="selectedTeamMemberId"
            aria-describedby="role-hint role-error"
            value={selectedTeamMemberId}
            onChange={e => {
              setSelectedTeamMemberId(e.target.value);
            }}
          >
            <option value="" disabled>
              Please select a person you would like to Edit or Remove
            </option>
            {teamMemberList.map(member => {
              return (
                <option key={member.value} value={member.value}>
                  {member.label}
                </option>
              );
            })}
          </select>
        </div>

        <div className="govuk-form-group govuk-!-width-full mr-5">
          <label className="govuk-label" htmlFor="selectRole">
            Choose role
          </label>

          <select
            className="govuk-select govuk-!-width-full"
            id="selectRole"
            name="selecttedRole"
            aria-describedby="role-hint role-error"
            value={selectedRole}
            onChange={e => setSelectedRole(e.target.value)}
          >
            <option value="" disabled>
              Please select a role
            </option>
            {roles.map(role => {
              return (
                <option key={role.id} value={role.value}>
                  {role.label}
                </option>
              );
            })}
          </select>
        </div>

        <JobTitle memberInfo={memberInfo} setMemberInfo={setMemberInfo} />
        <Speciality memberInfo={memberInfo} setMemberInfo={setMemberInfo} />
        <TeamName memberInfo={memberInfo} setMemberInfo={setMemberInfo} />
        <div className="govuk-button-group">
          <button
            type="submit"
            className="govuk-button"
            disabled={editTeamMemberStatus.loading}
            aria-disabled={editTeamMemberStatus.loading}
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditTeamMember;
